/**
 * Le rendu d'un document légal (docs/20 §10).
 *
 * **Un composant et trois contenus**, plutôt que trois pages écrites à la main.
 * Ces documents ont exactement la même forme — un titre, une date de dernière
 * mise à jour, des sections d'un titre et de quelques paragraphes — et la seule
 * chose qui les distingue est leur texte. Trois composants divergeraient sur la
 * typographie, et c'est celui qu'on relit le moins qui finirait mal.
 *
 * **Le texte vient de l'i18n, comme tout le reste** (R10). Ce n'est pas une
 * formalité ici : une politique de confidentialité affichée en français à un
 * utilisateur anglophone n'est pas un défaut cosmétique, c'est une information
 * qu'il n'a pas reçue.
 *
 * **`pending` marque ce qui reste à compléter par l'exploitant.** Les mentions
 * légales demandent une raison sociale, une adresse et un directeur de
 * publication ; les CGU, un droit applicable et un tribunal compétent. Ces
 * valeurs ne sont pas dans le code et ne peuvent pas y être devinées. Le
 * bandeau les réclame **visiblement**, plutôt que de laisser une page d'aplomb
 * qui aurait l'air complète — une page légale qui a l'air complète et ne l'est
 * pas est plus dangereuse qu'une page qui manque.
 */
import { useTranslation } from "react-i18next";

import { Alert } from "@/shared/ui/Alert";

export interface LegalSection {
  heading: string;
  paragraphs: string[];
}

interface Props {
  /** Clé du document dans le namespace `legal` : `notice`, `privacy`, `terms`. */
  document: "notice" | "privacy" | "terms";
}

export function LegalDocument({ document }: Props) {
  const { t, i18n } = useTranslation("legal");

  const sections = t(`${document}.sections`, { returnObjects: true }) as unknown as LegalSection[];
  const updated = t(`${document}.updated`);
  const pending = t(`${document}.pending`, { defaultValue: "" });

  const formatDate = (value: string) => {
    const parsed = new Date(value);
    return Number.isNaN(parsed.getTime())
      ? value
      : new Intl.DateTimeFormat(i18n.language, { dateStyle: "long" }).format(parsed);
  };

  return (
    // `max-w-prose` plutôt que la largeur du conteneur : au-delà d'environ
    // soixante-quinze caractères, l'œil perd sa ligne en revenant à la marge, et
    // ces pages sont les plus longues de l'application.
    <article className="mx-auto max-w-prose space-y-8 py-4">
      <header className="space-y-2">
        <h1 className="text-3xl font-semibold">{t(`${document}.title`)}</h1>
        <p className="text-sm text-slate-500 dark:text-slate-400">
          {t("updatedOn", { date: formatDate(updated) })}
        </p>
      </header>

      {pending !== "" && <Alert tone="warning">{pending}</Alert>}

      {sections.map((section) => (
        <section key={section.heading} className="space-y-3">
          <h2 className="text-xl font-semibold">{section.heading}</h2>
          {section.paragraphs.map((paragraph) => (
            <p key={paragraph} className="leading-relaxed text-slate-700 dark:text-slate-300">
              {paragraph}
            </p>
          ))}
        </section>
      ))}
    </article>
  );
}
